import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';

const AddBoardModal = ({ isOpen, onClose, onAdd, token }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client: '',
    projectType: 'Commercial',
    status: 'Active',
    baseCurrency: 'USD',
    supervisionFeePercent: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        onAdd(data.board);
        setFormData({ name: '', description: '', client: '', projectType: 'Commercial', status: 'Active', baseCurrency: 'USD', supervisionFeePercent: 0 });
        onClose();
      } else {
        const data = await response.json().catch(() => ({}));
        console.error('Board creation error:', response.status, data);
        setError(data.detail || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Network error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Create New Board</h2>
          <button className="modal-close" onClick={onClose} data-testid="close-modal">
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Board Name *</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="board-name-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="input"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="board-description-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Client</label>
              <input
                type="text"
                className="input"
                value={formData.client}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                data-testid="board-client-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Project Type</label>
              <select
                className="input"
                value={formData.projectType}
                onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                data-testid="board-type-select"
              >
                <option value="Commercial">Commercial</option>
                <option value="Residential">Residential</option>
                <option value="Interior Fit-out">Interior Fit-out</option>
                <option value="Renovation">Renovation</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Project Base Currency</label>
              <select
                className="input"
                value={formData.baseCurrency}
                onChange={(e) => setFormData({ ...formData, baseCurrency: e.target.value })}
                data-testid="board-base-currency-select"
              >
                <option value="USD">USD — US Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="TRY">TRY — Turkish Lira</option>
                <option value="AED">AED — UAE Dirham</option>
                <option value="SAR">SAR — Saudi Riyal</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="JPY">JPY — Japanese Yen</option>
                <option value="CNY">CNY — Chinese Yuan</option>
                <option value="INR">INR — Indian Rupee</option>
              </select>
              <p className="form-helper">All finance totals for this project will display in this currency. Tasks/payments in other currencies will be converted using their exchange rate.</p>
            </div>
            <div className="form-group">
              <label className="form-label">Supervision Fee (%)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                className="input"
                value={formData.supervisionFeePercent}
                onChange={(e) => setFormData({ ...formData, supervisionFeePercent: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                data-testid="board-supervision-fee"
              />
              <p className="form-helper">Optional. Added on top of all task costs to calculate the total project cost. Example: 10 means 10% supervision fee.</p>
            </div>
            {error && <div className="form-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading} data-testid="submit-board">
              {loading ? 'Creating...' : 'Create Board'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBoardModal;